import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { OrderingOption } from 'generated/graphql-types';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import { getCurrentUserIsTrueSystemUser } from 'src/app/authentication/state/authentication.selector';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { getCallExtension } from 'src/app/common-components/helpers/call-logs-helper';
import { PresenceCallDetailsHubService } from 'src/app/signalr/call-details-hub/presence-call-details-hub.service';
import { ExtensionPhoneHubService } from 'src/app/signalr/extension-phone-hub/extension-phone-hub.service';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { EditPresenceContactComponent } from '../../edit-presence-contact/containers/edit-presence-contact.component';
import { PresenceContactFilterOptions } from '../models/presence-contact-filter-option';
import { PresenceContactRegisterOptions } from '../models/presence-contact-register-options';
import {
   PresenceContactRegisterListItem,
   PresenceContactRegisterResults,
} from '../models/presence-contact-register-results.model';
import {
   GetPresenceContactRegister,
   GetPresenceContactRegister_extensionPhonesConnection_items,
} from '../queries/generated/GetPresenceContactRegister';
import { PresenceContactRegisterApiService } from '../services/presence-contact-register-api.service';
import * as PresenceContactRegisterActions from './presence-contact-register.actions';
import { presenceContactRegisterAdapterActions } from './presence-contact-register.actions';
import {
   PresenceContactRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectQuickFilters,
} from './presence-contact-register.reducer';
import * as PresenceContactRegisterSelectors from './presence-contact-register.selectors';
import { selectFilters } from './presence-contact-register.selectors';

@Injectable()
export class PresenceContactRegisterEffects {
   loadPresenceContactList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            presenceContactRegisterAdapterActions.pageEnter,
            PresenceContactRegisterActions.refreshList,
            PresenceContactRegisterActions.editPresenceContactSuccess
         ),
         withLatestFrom(this.store.pipe(select(selectFilters)), this.store.pipe(select(selectOrderingOptions))),
         switchMap(([, filters, orderingOptions]) => {
            return this.loadNewData(filters, null, orderingOptions);
         })
      );
   });

   loadPresenceContactPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(presenceContactRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(PresenceContactRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, orderingOptions, quickFilters]) => {
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(presenceContactRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(PresenceContactRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(presenceContactRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectFilters))
         ),
         switchMap(([action, quickfilters, orderingOptions, filters]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   registerAsViewingExtensionPhones$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(presenceContactRegisterAdapterActions.pageEnter),
            withLatestFrom(this.store.pipe(select(getSelectedAccountId))),
            tap(([_, accountId]) => {
               this.extensionPhoneHubService.registerExtensionPhoneEvents(accountId);
            })
         );
      },
      { dispatch: false }
   );

   registerAsViewingActiveCalls$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(presenceContactRegisterAdapterActions.pageEnter),
            withLatestFrom(
               this.store.pipe(select(getSelectedAccountId)),
               this.store.pipe(select(getCurrentUserIsTrueSystemUser))
            ),
            tap(([_, accountId, isSystemUser]) => {
               this.callDetailsHub.registerCallDetailEvents(accountId, isSystemUser);
            })
         );
      },
      { dispatch: false }
   );

   initiateDirectedCall$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(PresenceContactRegisterActions.initiateDirectedCall),
         switchMap((action) => {
            const message = 'Are you sure you want to call this contact?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Call Confirmation',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return of(
                        this.presenceContactRegisterApiService.initiateDirectedCall(action.initiateDirectedCallData)
                     ).pipe(map(() => PresenceContactRegisterActions.initiateDirectedCallSuccess()));
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   editPhone$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(PresenceContactRegisterActions.editPresenceContact),
            switchMap((action) => {
               const dialogRef = this.dialogService.openFormComponent(EditPresenceContactComponent, {
                  closeOnNavigation: true,
                  disableClose: true,
                  data: {
                     presenceContact: action.presenceContact,
                  },
                  panelClass: ['dialog-md', 'dialog-container'],
               });

               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private dialogService: LcsDialogService,
      private store: Store<PresenceContactRegisterState>,
      private presenceContactRegisterApiService: PresenceContactRegisterApiService,
      private extensionPhoneHubService: ExtensionPhoneHubService,
      private callDetailsHub: PresenceCallDetailsHubService,
      private confirmationService: ConfirmationDialogService,
      private dialog: MatDialog
   ) {}

   private loadNewData(
      filters: PresenceContactFilterOptions[],
      quickFilters: PresenceContactRegisterOptions | null = null,
      orderingOptions: OrderingOption[] | null = null
   ) {
      return this.presenceContactRegisterApiService
         .getPresenceContacts(null, filters, quickFilters, orderingOptions)
         .pipe(
            map((result) => {
               return presenceContactRegisterAdapterActions.loadSuccess(
                  this.convertGetPresenceContactResultToLoadSuccessProps(result.data)
               );
            }),
            catchError((error) => {
               return of(presenceContactRegisterAdapterActions.loadFailure(error));
            })
         );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: PresenceContactFilterOptions[],
      quickFilters: PresenceContactRegisterOptions | null = null,
      orderingOptions: OrderingOption[] | null = null
   ) {
      return this.presenceContactRegisterApiService
         .getPresenceContacts(endCursor, filters, quickFilters, orderingOptions)
         .pipe(
            map((result) => {
               return presenceContactRegisterAdapterActions.loadPageSuccess(
                  this.convertGetPresenceContactResultToLoadSuccessProps(result.data)
               );
            }),
            catchError((error) => {
               return of(presenceContactRegisterAdapterActions.loadPageFailure(error));
            })
         );
   }

   private convertGetPresenceContactResultToLoadSuccessProps(
      getPresenceContact: GetPresenceContactRegister
   ): PresenceContactRegisterResults {
      return {
         endCursor: getPresenceContact.extensionPhonesConnection.pageInfo.endCursor,
         hasNextPage: getPresenceContact.extensionPhonesConnection.pageInfo.hasNextPage,
         items: getPresenceContact.extensionPhonesConnection.items.map(this.convertPresenceContactRegisterListItem),
         totalCount: getPresenceContact.extensionPhonesConnection.totalCount,
      };
   }

   private convertPresenceContactRegisterListItem(
      getPresenceContactItem: GetPresenceContactRegister_extensionPhonesConnection_items
   ): PresenceContactRegisterListItem {
      // getPresenceContactItem.callDetail?.callSegmentsConnection.items.forEach((callSegment) => {
      //    callSegment.duration = Math.floor((new Date().getTime() - new Date(callSegment.startDate).getTime()) / 1000);
      // });
      return {
         ...getPresenceContactItem,
         onCall: getPresenceContactItem.callDetail?.callDetailId ? true : false,
         departmentExtensions: getPresenceContactItem.extension.departmentExtensionsConnection?.items.map(
            (departmentExtension) => {
               return {
                  departmentExtensionId: departmentExtension.departmentExtensionId,
                  extensionId: departmentExtension.extensionId,
                  departmentId: departmentExtension.departmentId,
                  department: {
                     departmentId: departmentExtension.department?.departmentId,
                     name: departmentExtension.department?.name,
                     isActive: departmentExtension.department?.isActive,
                     accountId: departmentExtension.department?.accountId,
                  },
                  isDeleted: false,
               };
            }
         ),
         callDetail: {
            callDetailId: getPresenceContactItem.callDetail?.callDetailId,
            callDestination: getCallExtension(
               { ...getPresenceContactItem.callDetail?.destinationExtension },
               getPresenceContactItem.callDetail?.callDirectionTypeId,
               getPresenceContactItem.callDetail?.destinationNumber ?? null,
               true
            ),
            callDirection: getPresenceContactItem.callDetail?.callDirectionTypeId ?? null,
            callSegmentsConnection: getPresenceContactItem.callDetail?.callSegmentsConnection ?? null,
            callSource: getCallExtension(
               { ...getPresenceContactItem.callDetail?.sourceExtension },
               getPresenceContactItem.callDetail?.callDirectionTypeId,
               getPresenceContactItem.callDetail?.sourceNumber ?? null
            ),
            destinationExtension: getPresenceContactItem.callDetail?.destinationExtension ?? null,
            destinationNumber: getPresenceContactItem.callDetail?.destinationNumber ?? null,
            phoneNumber: getPresenceContactItem.callDetail?.phoneNumber?.phoneNumber ?? null,
            serverName: getPresenceContactItem.callDetail?.serverName ?? null,
            sipCallId: getPresenceContactItem.callDetail?.sipCallId ?? null,
            startTime: getPresenceContactItem.callDetail?.startTime,
            sourceExtension: getPresenceContactItem.callDetail?.sourceExtension ?? null,
         },
      };
   }
}
